<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import {
    formatReservationPhone,
    reservationPhoneDigits,
  } from '@/domain/reservations/reservation-phone';
  import type { IReservation } from '@/interfaces/IReservation';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  const props = defineProps<{ reservation: IReservation }>();
  const emit = defineEmits<{ (event: 'guest'): void }>();
  const restaurant = useRestaurantStore();
  const phone = computed(() =>
    formatReservationPhone(
      props.reservation.guestPhone,
      restaurant.restaurant?.country ?? 'AR'
    )
  );
  const phoneDigits = computed(() =>
    reservationPhoneDigits(props.reservation.guestPhone ?? '')
  );
</script>
<template>
  <section class="reservation-guest-card">
    <div class="section-heading">
      <h3>{{ $t('reservations.detail.guest') }}</h3>
      <PosUiButton
        v-if="reservation.customerId"
        color="neutral"
        variant="ghost"
        @click="emit('guest')"
        >{{ $t('reservations.detail.viewGuest') }}</PosUiButton
      >
    </div>
    <p v-if="phone" class="contact-value">{{ phone }}</p>
    <p v-if="reservation.guestEmail" class="contact-value">
      {{ reservation.guestEmail }}
    </p>
    <div v-if="phoneDigits" class="contact-actions">
      <a
        :href="`https://wa.me/${phoneDigits}`"
        target="_blank"
        rel="noopener noreferrer"
        ><PosUiIcon name="whatsapp" />{{
          $t('reservations.detail.whatsapp')
        }}</a
      ><a :href="`tel:+${phoneDigits}`"
        ><PosUiIcon name="phone-outline" />{{
          $t('reservations.detail.call')
        }}</a
      >
    </div>
    <p v-if="reservation.customer" class="guest-counts">
      {{
        $t('reservations.detail.visits', {
          count: reservation.customer.visitsCount,
        })
      }}
      ·
      {{
        $t('reservations.detail.noShows', {
          count: reservation.customer.noShowsCount,
        })
      }}
      ·
      {{
        $t('reservations.detail.cancellations', {
          count: reservation.customer.cancellationsCount,
        })
      }}
    </p>
    <p v-if="reservation.customer?.reservationNotes" class="guest-excerpt">
      {{ reservation.customer.reservationNotes }}
    </p>
  </section>
</template>
<style scoped lang="scss">
  .reservation-guest-card {
    @apply tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;
    h3 {
      @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    .section-hint {
      @apply tw-mt-1 tw-text-xs tw-text-neutral-400;
    }
    .muted {
      @apply tw-text-sm tw-text-neutral-500;
    }
    .section-heading {
      @apply tw-mb-2 tw-flex tw-min-h-[44px] tw-items-center tw-justify-between tw-gap-2;
    }
    .contact-value {
      @apply tw-break-words tw-text-sm tw-text-neutral-700;
    }
    .contact-actions {
      @apply tw-mt-2 tw-flex tw-gap-2;
      a {
        @apply tw-inline-flex tw-min-h-[44px] tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-neutral-200 tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-700 hover:tw-bg-neutral-100;
      }
    }
    .guest-counts {
      @apply tw-mt-3 tw-text-xs tw-text-neutral-500;
    }
    .guest-excerpt {
      @apply tw-mt-2 tw-whitespace-pre-wrap tw-rounded-lg tw-bg-neutral-100 tw-p-3 tw-text-xs tw-text-neutral-600;
    }
  }
</style>
